import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "@/components/ImageUploader";
import CategorySubcategoryPicker from "@/components/CategorySubcategoryPicker";
import { toast } from "sonner";

export default function AdminOfferForm() {
  const { id } = useParams();
  const [form, setForm] = useState({
    title: "", description: "", category_id: "", subcategory_id: "", city: "",
    image: "", images: [], discount: "", advantage_type: "percentage", conditions: "", end_date: "",
    promo_code: "", max_redemptions: 0, coverage: "local",
  });
  const [status, setStatus] = useState("pending");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  useEffect(() => {
    api.get("/admin/cities").then((r) => setCities(r.data));
    api.get(`/admin/offers/${id}`).then((r) => {
      const o = r.data;
      setForm({
        title: o.title || "", description: o.description || "",
        category_id: o.category_id || "", subcategory_id: o.subcategory_id || "", city: o.city || "",
        image: o.image || "",
        images: Array.isArray(o.images) && o.images.length ? o.images : (o.image ? [o.image] : []),
        discount: o.discount || "",
        advantage_type: o.advantage_type || "percentage",
        conditions: o.conditions || "",
        end_date: o.end_date || "",
        promo_code: o.promo_code || "", max_redemptions: o.max_redemptions || 0,
        coverage: o.coverage || "local",
      });
      setStatus(o.status || "pending");
    }).catch(() => { toast.error("Offre introuvable"); navigate("/admin/offres"); });
  }, [id, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/admin/offers/${id}`, form);
      // Preserve/update status separately if changed
      await api.patch(`/admin/offers/${id}/moderate`, { status });
      toast.success("Offre modifiée");
      navigate("/admin/offres");
    } catch { toast.error("Erreur d'enregistrement"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold" data-testid="admin-offer-form-title">Modifier l'offre</h1>
      <p className="text-sm text-slate-500 mt-1">Vous pouvez ajuster tous les champs et changer le statut de publication.</p>
      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 space-y-4 max-w-3xl" data-testid="admin-offer-form">
        <div>
          <Label>Statut</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="admin-offer-status"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="published">Publiée</SelectItem>
              <SelectItem value="suspended">Suspendue</SelectItem>
              <SelectItem value="rejected">Refusée</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Zone de couverture</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button type="button" onClick={()=>setForm((s)=>({...s, coverage: "local"}))}
                    className={`p-3 rounded-xl border text-left text-sm ${form.coverage==="local"?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white"}`}>
              <div className="font-semibold">Ville / pôle</div>
            </button>
            <button type="button" onClick={()=>setForm((s)=>({...s, coverage: "department"}))}
                    className={`p-3 rounded-xl border text-left text-sm ${form.coverage==="department"?"border-[#E05638] bg-orange-50":"border-slate-200 bg-white"}`}>
              <div className="font-semibold">Tout l'Eure-et-Loir</div>
            </button>
          </div>
        </div>
        <div><Label>Titre</Label><Input required value={form.title} onChange={set("title")} data-testid="admin-offer-title"/></div>
        <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={set("description")}/></div>
        <CategorySubcategoryPicker
          value={{ category_id: form.category_id, subcategory_id: form.subcategory_id }}
          onChange={({ category_id, subcategory_id }) =>
            setForm((s) => ({ ...s, category_id, subcategory_id }))}
          testIdPrefix="admin-offer"
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Ville</Label>
            <Select value={form.city} onValueChange={(v)=>setForm((s)=>({...s, city: v}))}>
              <SelectTrigger><SelectValue placeholder="Choisir"/></SelectTrigger>
              <SelectContent>{cities.map((c)=>(<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div><Label>Réduction</Label><Input required value={form.discount} onChange={set("discount")}/></div>
          <div>
            <Label>Type d'avantage</Label>
            <Select value={form.advantage_type} onValueChange={(v)=>setForm((s)=>({...s, advantage_type: v}))}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Pourcentage</SelectItem>
                <SelectItem value="fixed">Réduction fixe</SelectItem>
                <SelectItem value="formula">Formule</SelectItem>
                <SelectItem value="gift">Cadeau</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Conditions</Label><Textarea rows={3} value={form.conditions} onChange={set("conditions")}/></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Date de fin</Label>
            <Input type="date" data-testid="admin-offer-edit-end-date" value={form.end_date ? form.end_date.slice(0, 10) : ""}
                   onChange={(e)=>setForm((s)=>({...s, end_date: e.target.value ? new Date(e.target.value + "T23:59:59").toISOString() : ""}))}/>
          </div>
          <div><Label>Code promo</Label><Input value={form.promo_code} onChange={set("promo_code")}/></div>
        </div>
        <div>
          <Label>Images de l'offre (jusqu'à 5)</Label>
          <div className="mt-2">
            <ImageUploader
              value={form.images}
              onChange={(imgs)=>setForm((s)=>({...s, images: imgs, image: imgs[0] || ""}))}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <Button type="submit" disabled={loading} className="btn-shine text-white" data-testid="admin-offer-save-btn">
            {loading ? "Enregistrement…" : "Enregistrer"}
          </Button>
          <Button type="button" variant="outline" onClick={()=>navigate("/admin/offres")}>Annuler</Button>
        </div>
      </form>
    </div>
  );
}
